import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { EmptyState } from "@/components/admin/payouts/EmptyState";
import { throwIfNotOk } from "@/lib/api-error";
import { Plus, MagnifyingGlass, Notepad } from "@phosphor-icons/react";
import { DOCUMENT_TYPE_GROUPS, type DocumentSummary } from "@/types";
import {
  DocumentsTable,
  DocumentFormDialog,
  DocumentsSkeleton,
  type DocumentFormSubmit,
  type GroupFilter,
} from "@/components/admin/documents";

export const Route = createFileRoute("/dashboard/admin/documents/")({
  component: AdminDocuments,
});

const GROUP_OPTIONS: { value: GroupFilter; label: string }[] = [
  { value: "all", label: "Svi" },
  ...DOCUMENT_TYPE_GROUPS.map((g) => ({ value: g.label, label: g.label })),
];

function AdminDocuments() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [group, setGroup] = useState<GroupFilter>("all");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "documents"],
    queryFn: async () => {
      const res = await fetch("/api/documents");
      await throwIfNotOk(res, "Greška pri učitavanju dokumenata");
      return (await res.json()) as { items: DocumentSummary[] };
    },
  });

  const createMutation = useMutation({
    mutationFn: async (form: DocumentFormSubmit) => {
      const res = await fetch("/api/documents", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, contentJson: { type: "doc", content: [] } }),
      });
      await throwIfNotOk(res, "Greška pri kreiranju dokumenta");
      return (await res.json()) as DocumentSummary;
    },
    onSuccess: (doc) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "documents"] });
      setFormOpen(false);
      navigate({ to: "/dashboard/admin/documents/$documentId", params: { documentId: doc.id } });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/documents/${id}`, { method: "DELETE" });
      await throwIfNotOk(res, "Greška pri brisanju dokumenta");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "documents"] }),
  });

  const visible = useMemo(() => {
    const items = data?.items ?? [];
    const inGroup =
      group === "all"
        ? items
        : items.filter((d) =>
            DOCUMENT_TYPE_GROUPS.find((g) => g.label === group)?.types.includes(d.type),
          );
    const q = search.trim().toLowerCase();
    return q ? inGroup.filter((d) => d.title.toLowerCase().includes(q)) : inGroup;
  }, [data, group, search]);

  const hasFilters = group !== "all" || search.trim().length > 0;
  const clearFilters = () => {
    setGroup("all");
    setSearch("");
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Dokumenti
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Ugovori, saglasnosti, pravni akti i obrasci.
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)} className="shrink-0">
          <Plus className="size-4" />
          Novi dokument
        </Button>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SegmentedFilter ariaLabel="Grupa dokumenta" options={GROUP_OPTIONS} value={group} onChange={setGroup} />
        <div className="relative max-w-xs flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Pretraga po nazivu"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <DocumentsSkeleton />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Notepad}
          title="Nema dokumenata"
          description="Kreirajte prvi ugovor, saglasnost ili pravilnik."
          hasFilters={hasFilters}
          onClear={clearFilters}
        />
      ) : (
        <DocumentsTable documents={visible} onDelete={(id) => deleteMutation.mutate(id)} />
      )}

      <DocumentFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmit={(form) => createMutation.mutate(form)}
        isPending={createMutation.isPending}
      />
    </div>
  );
}
